import { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTypedParams, useTypedState } from 'react-router-typesafe-routes/dom';

import { useLessonTasks } from 'features/TaskSelection/queries';
import { ROUTES } from 'navigation/constants';

export const useTaskService = () => {
  const { id: taskId } = useTypedParams(ROUTES.TASK);
  const { lessonId } = useTypedState(ROUTES.TASK);
  const location = useLocation();
  
  // Extract additional state from navigation
  const { skipTaskSelection, taskName, taskType } = (location.state as any) || {};

  const navigate = useNavigate();

  useEffect(() => {
    if (!lessonId) {
      navigate(ROUTES.HOME.path);
    }
  }, [lessonId]);

  // Skip loading lesson tasks for onboarding
  const isOnboarding = taskType === 'onboarding' && skipTaskSelection;
  const lessonTasksData = useLessonTasks(isOnboarding ? undefined : lessonId);

  const task = useMemo(() => {
    // For onboarding, create a synthetic task object
    if (isOnboarding) {
      return {
        id: taskId,
        name: taskName || 'Getting to Know You',
        max_points: 600,
        type: taskType || 'onboarding',
      };
    }

    // Regular task loading
    return (
      (!lessonTasksData.isLoading &&
        lessonTasksData.data &&
        lessonTasksData.data.length &&
        lessonTasksData.data.find(({ id }) => taskId === id)) ||
      null
    );
  }, [lessonTasksData, taskId, isOnboarding, taskName, taskType]);

  useEffect(() => {
    // Skip validation for onboarding
    if (isOnboarding) return;

    if (!lessonTasksData.isLoading) {
      if (lessonTasksData.data?.length) {
        const currentTaskIndex = lessonTasksData.data.findIndex(({ id }) => id === taskId);

        if (currentTaskIndex >= 0) {
        } else {
          if (lessonId) {
            navigate(ROUTES.LESSON.buildPath({ id: lessonId }));
          } else {
            navigate(ROUTES.HOME.path);
          }
        }
      } else {
        navigate(ROUTES.HOME.path);
      }
    }
  }, [lessonTasksData, isOnboarding]);

  return {
    isLoading: isOnboarding ? false : lessonTasksData.isLoading,
    taskId,
    lessonId: lessonId as string,
    title: task?.name ?? 'New word',
    maxPoints: task?.max_points ?? 0,
    taskType: task?.type ?? '',
  };
};
